import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Search, MapPin, GraduationCap, DollarSign, 
  Users, Award, Building2, Phone, Mail, Globe, X, Filter,
  Star, Calendar, CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api` 
  : 'http://localhost:8001/api';

const TopColleges = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [colleges, setColleges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [states, setStates] = useState([]);
  const [types, setTypes] = useState([]);
  const [countries, setCountries] = useState([]);
  const [selectedCollege, setSelectedCollege] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalColleges, setTotalColleges] = useState(0);
  const [pageSize] = useState(30);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    fetchFilters();
  }, []);

  useEffect(() => {
    fetchColleges();
  }, [debouncedSearch, selectedState, selectedType, selectedCountry, currentPage]);

  const fetchColleges = async () => {
    if (currentPage === 1) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    
    try {
      const params = new URLSearchParams();
      params.append("page", currentPage);
      params.append("limit", pageSize);
      
      if (debouncedSearch) params.append("search", debouncedSearch);
      if (selectedCountry) params.append("country", selectedCountry);
      if (selectedState) params.append("state", selectedState);
      if (selectedType) params.append("type", selectedType);
      
      const response = await axios.get(`${API}/colleges?${params.toString()}`);
      setColleges(response.data.colleges || []);
      setTotalColleges(response.data.total || 0);
      setTotalPages(response.data.total_pages || 1);
    } catch (error) {
      console.error('Error fetching colleges:', error);
      toast.error('Failed to load colleges');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const fetchFilters = async () => {
    try {
      const [statesRes, typesRes, countriesRes] = await Promise.all([
        axios.get(`${API}/colleges/states/list`),
        axios.get(`${API}/colleges/types/list`),
        axios.get(`${API}/colleges/countries/list`)
      ]);
      setStates(statesRes.data.states || []);
      setTypes(typesRes.data.types || []);
      setCountries(countriesRes.data.countries || []);
    } catch (error) {
      console.error('Error fetching filters:', error);
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedState('');
    setSelectedType('');
    setSelectedCountry('');
    setCurrentPage(1);
  };

  const hasFilters = searchTerm || selectedState || selectedType || selectedCountry;

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="p-8 text-center">
          <GraduationCap className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
          <h2 className="text-2xl font-bold mb-2">Login Required</h2>
          <p className="text-muted-foreground mb-4">Please login to view colleges</p>
          <Button onClick={() => navigate('/login')}>Login</Button>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading colleges...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2 flex items-center gap-2">
          <Building2 className="w-8 h-8 text-primary" />
          Top Universities & Colleges Worldwide
        </h1>
        <p className="text-muted-foreground">
          Explore premier educational institutions from {countries.length} countries ({totalColleges.toLocaleString()} universities)
        </p>
      </div>

      {/* Search and Filters */}
      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search universities by name, location, or country..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Filter Button (Mobile) */}
            <Button
              variant="outline"
              className="md:hidden"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
            </Button>

            {/* Filters (Desktop) */}
            <div className="hidden md:flex gap-4 flex-wrap">
              <select
                value={selectedCountry}
                onChange={(e) => setSelectedCountry(e.target.value)}
                className="px-4 py-2 border rounded-md bg-white dark:bg-slate-800"
                data-testid="country-filter"
              >
                <option value="">All Countries</option>
                {countries.map(country => (
                  <option key={country.name} value={country.name}>
                    {country.name} ({country.count})
                  </option>
                ))}
              </select>

              <select
                value={selectedState}
                onChange={(e) => setSelectedState(e.target.value)}
                className="px-4 py-2 border rounded-md bg-white dark:bg-slate-800"
              >
                <option value="">All States/Regions</option>
                {states.map(state => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>

              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="px-4 py-2 border rounded-md bg-white dark:bg-slate-800"
              >
                <option value="">All Types</option>
                {types.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>

              {(searchTerm || selectedState || selectedType || selectedCountry) && (
                <Button variant="ghost" onClick={clearFilters}>
                  <X className="w-4 h-4 mr-2" />
                  Clear
                </Button>
              )}
            </div>
          </div>

          {/* Mobile Filters */}
          {showFilters && (
            <div className="mt-4 md:hidden space-y-3">
              <select
                value={selectedCountry}
                onChange={(e) => setSelectedCountry(e.target.value)}
                className="w-full px-4 py-2 border rounded-md bg-white dark:bg-slate-800"
              >
                <option value="">All Countries</option>
                {countries.map(country => (
                  <option key={country.name} value={country.name}>
                    {country.name} ({country.count})
                  </option>
                ))}
              </select>

              <select
                value={selectedState}
                onChange={(e) => setSelectedState(e.target.value)}
                className="w-full px-4 py-2 border rounded-md bg-white dark:bg-slate-800"
              >
                <option value="">All States/Regions</option>
                {states.map(state => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>

              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="w-full px-4 py-2 border rounded-md bg-white"
              >
                <option value="">All Types</option>
                {types.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>

              {(searchTerm || selectedState || selectedType || selectedCountry) && (
                <Button variant="ghost" onClick={clearFilters} className="w-full">
                  <X className="w-4 h-4 mr-2" />
                  Clear Filters
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Results Count */}
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-muted-foreground">
          Showing {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, totalColleges)} of {totalColleges.toLocaleString()} universities
        </p>
        <p className="text-sm text-muted-foreground">
          Page {currentPage} of {totalPages}
        </p>
      </div>

      {/* Colleges Grid */}
      {loadingMore ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : colleges.length === 0 ? (
        <Card className="p-8 text-center">
          <GraduationCap className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-xl font-semibold mb-2">No Colleges Found</h3>
          <p className="text-muted-foreground mb-4">Try adjusting your filters</p>
          <Button variant="outline" onClick={clearFilters}>Clear Filters</Button>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {colleges.map((college) => (
            <Card 
              key={college.id} 
              className="overflow-hidden hover:shadow-xl transition-shadow cursor-pointer group"
              onClick={() => setSelectedCollege(college)}
            >
              {/* College Image */}
              <div className="relative h-48 bg-gradient-to-br from-gray-100 to-gray-200 overflow-hidden">
                {college.image ? (
                  <img
                    src={`${college.image}?w=400&h=300&fit=crop&auto=format&q=60`}
                    alt={college.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-100 to-purple-100"><span class="text-4xl">🎓</span></div>';
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <GraduationCap className="w-16 h-16 text-gray-400" />
                  </div>
                )}
                {college.is_featured && (
                  <Badge className="absolute top-2 right-2 bg-yellow-500 hover:bg-yellow-600">
                    <Star className="w-3 h-3 mr-1 fill-current" />
                    Featured
                  </Badge>
                )}
                {college.ranking?.nirf && (
                  <Badge className="absolute top-2 left-2 bg-blue-600">
                    NIRF Rank: {college.ranking.nirf}
                  </Badge>
                )}
              </div>

              <CardContent className="p-5">
                <div className="mb-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant="outline">{college.type}</Badge>
                    {college.country && (
                      <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                        {college.country}
                      </Badge>
                    )}
                  </div>
                  <h3 className="font-bold text-lg line-clamp-2 mb-2">{college.name}</h3>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <MapPin className="w-4 h-4" />
                    <span className="line-clamp-1">{college.location || `${college.city}, ${college.state}`}</span>
                  </div>
                </div>

                <div className="space-y-2 mb-4">
                  {college.ranking?.qs_world && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">QS World Rank</span>
                      <span className="font-semibold text-blue-600">#{college.ranking.qs_world}</span>
                    </div>
                  )}
                  {college.placements?.average_package && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Avg. Package</span>
                      <span className="font-semibold text-green-600">{college.placements.average_package}</span>
                    </div>
                  )}
                  {college.placements?.placement_rate && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Placement Rate</span>
                      <span className="font-semibold">{college.placements.placement_rate}</span>
                    </div>
                  )}
                </div>

                <Button className="w-full" variant="outline">
                  View Details
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-4 mt-8">
          <Button
            variant="outline"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(p => p - 1)}
          >
            Previous
          </Button>
          
          <div className="flex items-center gap-2">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              
              if (pageNum < 1 || pageNum > totalPages) return null;
              
              return (
                <Button
                  key={pageNum}
                  variant={currentPage === pageNum ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setCurrentPage(pageNum)}
                >
                  {pageNum}
                </Button>
              );
            })}
          </div>
          
          <Button
            variant="outline"
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(p => p + 1)}
          >
            Next
          </Button>
        </div>
      )}

      {/* College Detail Modal */}
      <Dialog open={!!selectedCollege} onOpenChange={() => setSelectedCollege(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {selectedCollege && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl pr-8">{selectedCollege.name}</DialogTitle>
              </DialogHeader>

              {/* College Image */}
              {selectedCollege.image && (
                <div className="w-full h-64 bg-gray-100 rounded-lg overflow-hidden mb-6">
                  <img
                    src={`${selectedCollege.image}?w=800&h=400&fit=crop&auto=format&q=70`}
                    alt={selectedCollege.name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              )}

              <Tabs defaultValue="overview" className="w-full">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="overview">Overview</TabsTrigger>
                  <TabsTrigger value="admission">Admission</TabsTrigger>
                  <TabsTrigger value="placements">Placements</TabsTrigger>
                  <TabsTrigger value="contact">Contact</TabsTrigger>
                </TabsList>

                {/* Overview Tab - Content truncated for token limit */}
                <TabsContent value="overview" className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-gray-50 dark:bg-slate-800 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <MapPin className="w-5 h-5 text-primary" />
                        <span className="font-semibold">Location</span>
                      </div>
                      <p className="text-sm text-muted-foreground">{selectedCollege.location || `${selectedCollege.city}, ${selectedCollege.state}`}</p>
                      {selectedCollege.country && (
                        <Badge variant="secondary" className="mt-2">{selectedCollege.country}</Badge>
                      )}
                    </div>
                    <div className="p-4 bg-gray-50 dark:bg-slate-800 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Calendar className="w-5 h-5 text-primary" />
                        <span className="font-semibold">Established</span>
                      </div>
                      <p className="text-sm text-muted-foreground">{selectedCollege.established}</p>
                    </div>
                  </div>

                  {/* Rankings */}
                  {selectedCollege.ranking && (
                    <div>
                      <h4 className="font-semibold mb-3 flex items-center gap-2">
                        <Award className="w-5 h-5 text-primary" />
                        Rankings
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedCollege.ranking.qs_world && (
                          <Badge variant="outline" className="py-2 px-3">
                            <span className="font-normal mr-1">QS World:</span>
                            <span className="font-bold text-blue-600">#{selectedCollege.ranking.qs_world}</span>
                          </Badge>
                        )}
                        {selectedCollege.ranking.times_higher && (
                          <Badge variant="outline" className="py-2 px-3">
                            <span className="font-normal mr-1">Times Higher:</span>
                            <span className="font-bold text-purple-600">#{selectedCollege.ranking.times_higher}</span>
                          </Badge>
                        )}
                        {selectedCollege.ranking.us_news && (
                          <Badge variant="outline" className="py-2 px-3">
                            <span className="font-normal mr-1">US News:</span>
                            <span className="font-bold text-green-600">#{selectedCollege.ranking.us_news}</span>
                          </Badge>
                        )}
                        {selectedCollege.ranking.nirf && (
                          <Badge variant="outline" className="py-2 px-3">
                            <span className="font-normal mr-1">NIRF:</span>
                            <span className="font-bold text-orange-600">#{selectedCollege.ranking.nirf}</span>
                          </Badge>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Courses Offered */}
                  {selectedCollege.courses_offered && selectedCollege.courses_offered.length > 0 && (
                    <div>
                      <h4 className="font-semibold mb-3 flex items-center gap-2">
                        <GraduationCap className="w-5 h-5 text-primary" />
                        Courses Offered
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedCollege.courses_offered.map((course, idx) => (
                          <Badge key={idx} variant="outline">{course}</Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Facilities */}
                  {selectedCollege.facilities && selectedCollege.facilities.length > 0 && (
                    <div>
                      <h4 className="font-semibold mb-3">Facilities</h4>
                      <div className="grid grid-cols-2 gap-2">
                        {selectedCollege.facilities.map((facility, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-sm">
                            <CheckCircle2 className="w-4 h-4 text-green-600" />
                            <span>{facility}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </TabsContent>

                {/* Other tabs similar to above */}
                <TabsContent value="admission" className="space-y-4">
                  <p className="text-sm text-muted-foreground">Admission details here...</p>
                </TabsContent>

                <TabsContent value="placements" className="space-y-4">
                  <p className="text-sm text-muted-foreground">Placement details here...</p>
                </TabsContent>

                <TabsContent value="contact" className="space-y-4">
                  {selectedCollege.website && (
                    <Button 
                      className="w-full" 
                      size="lg"
                      onClick={() => window.open(selectedCollege.website, '_blank')}
                    >
                      Visit Official Website
                    </Button>
                  )}
                </TabsContent>
              </Tabs>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TopColleges;
